import React from "react";
import { Card } from "../../../components";
import type { LoadSource, SourceType } from "../domain/types";
import { SOURCE_TYPE_LABELS, SOURCE_TYPE_COLORS } from "../domain/types";

interface Props {
  sources: LoadSource[];
  loadMw: number;
}

interface TypeAgg {
  type: SourceType;
  mw: number;
  mwh?: number; // BESS only
  durationH?: number;
}

export function LoadCapacityBar({ sources, loadMw }: Props) {
  // Aggregate by source_type
  const aggMap = new Map<SourceType, TypeAgg>();
  for (const src of sources) {
    if (!src.is_active) continue;
    const t = src.source_type as SourceType;
    const existing = aggMap.get(t);
    if (existing) {
      existing.mw += src.nameplate_capacity_mw;
      if (t === "bess" && src.bess_capacity_mwh)
        existing.mwh = (existing.mwh || 0) + src.bess_capacity_mwh;
    } else {
      aggMap.set(t, {
        type: t,
        mw: src.nameplate_capacity_mw,
        ...(t === "bess" && {
          mwh: src.bess_capacity_mwh || 0,
          durationH: src.bess_duration_hours || 4,
        }),
      });
    }
  }

  const aggs = Array.from(aggMap.values());
  const totalCapacity = aggs.reduce((s, a) => s + a.mw, 0);
  const barMax = Math.max(totalCapacity, loadMw) * 1.1 || 1;

  // Ordered: solar, wind, bess, ng
  const order: SourceType[] = ["solar", "wind", "bess", "ng"];
  const sorted = aggs.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));

  return (
    <Card>
      <div className="p-4">
        <h4 className="text-body font-medium text-text-primary mb-3">
          Installed Capacity
        </h4>

        {/* Stacked bar */}
        <div className="relative h-8 bg-surface-600 rounded-lg overflow-hidden">
          {sorted.map((agg) => {
            const widthPct = (agg.mw / barMax) * 100;
            return (
              <div
                key={agg.type}
                className="h-full float-left flex items-center justify-center text-[11px] font-medium text-white/90"
                style={{
                  width: `${widthPct}%`,
                  backgroundColor: SOURCE_TYPE_COLORS[agg.type],
                  minWidth: widthPct > 0 ? "2px" : 0,
                }}
                title={`${SOURCE_TYPE_LABELS[agg.type]}: ${agg.mw.toLocaleString()} MW`}
              >
                {widthPct > 8
                  ? `${SOURCE_TYPE_LABELS[agg.type]} ${Math.round(agg.mw)}`
                  : ""}
              </div>
            );
          })}

          {/* Load marker */}
          {loadMw > 0 && (
            <div
              className="absolute top-0 h-full border-l-2 border-dashed border-white/70"
              style={{ left: `${(loadMw / barMax) * 100}%` }}
              title={`Load: ${loadMw.toLocaleString()} MW`}
            >
              <span className="absolute -top-5 -translate-x-1/2 text-[10px] text-text-muted whitespace-nowrap">
                Load {Math.round(loadMw)} MW
              </span>
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-body-sm">
          {sorted.map((agg) => (
            <span key={agg.type} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: SOURCE_TYPE_COLORS[agg.type] }}
              />
              <span className="text-text-muted">{SOURCE_TYPE_LABELS[agg.type]}:</span>
              <span className="text-text-primary font-medium">
                {Math.round(agg.mw).toLocaleString()} MW
                {agg.type === "bess" && agg.mwh
                  ? ` / ${Math.round(agg.mwh).toLocaleString()} MWh`
                  : ""}
              </span>
            </span>
          ))}
          <span className="ml-auto text-text-muted">
            Total:{" "}
            <span className="text-text-primary font-medium">
              {Math.round(totalCapacity).toLocaleString()} MW
            </span>
            {loadMw > 0 && (
              <>
                {" "}&middot; Load:{" "}
                <span className="text-text-primary font-medium">
                  {Math.round(loadMw).toLocaleString()} MW
                </span>
              </>
            )}
          </span>
        </div>
      </div>
    </Card>
  );
}
